'use client'
import { useContext, useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { TrackingWrapper } from '../Tracking/TrackingWrapper'
import { X } from 'react-feather'
import EnvelopeOk from 'components/Svgs/EnvelopeOk'
import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import { BLOCKS } from '@contentful/rich-text-types'
import { Button } from '@achieve/ascend'
import { useAppRouter } from '@/hooks/useAppRouter/useAppRouter'
import logger from '@achieve/cx-logger'

export const DirectMailPzn = function () {
  const PZN_CALL_COMPLETE_COOKIE = 'pzn_call_complete'
  const { fireAndForget } = useTriggerEvents()
  const pznCompleted = Cookies.get(PZN_CALL_COMPLETE_COOKIE)
  const [showModal, setShowModal] = useState(false)
  const { asPath } = useAppRouter()
  const {
    state: { isPageLoaded },
  } = useContext(AnalyticsContext)

  useEffect(() => {
    if (!pznCompleted) {
      let retryCount = 0
      const MAX_RETRIES = 5
      const INITIAL_DELAY = 1500
      let timerId = null

      const sendData = async () => {
        const ehVisitId = Cookies.get('eh_visit_id')
        const browserId = Cookies.get('browser_id')
        try {
          if (ehVisitId && browserId) {
            const data = {
              visit_id: ehVisitId,
              browser_id: browserId,
              page_id: asPath,
              test_id: 'repeat',
              launch_darkly: 'ACX-WEB',
              campaign_id: 'ahl_dm_repeat',
            }
            const JSONdata = JSON.stringify(data)
            const options = {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSONdata,
            }
            const response = await fetch('/api/achieve-tools/assessment/site-pzn', options)
            const result = await response.json()
            if (result?.eligibility_indicator !== 'Y') {
              fireAndForget({
                event_type: 'track',
                event_action: 'ahl_ahl_dm_service_call_failed',
                event: 'ahl_ahl_dm_service_call_failed',
                event_description: result,
              })
            } else {
              fireAndForget({
                event_type: 'track',
                event_action: 'ahl_dm_service_call_success',
                event: 'ahl_dm_service_call_success',
                event_description: result,
              })
              // setShowModal(true) Avoid the show modal until further instructions.
            }
            Cookies.set(PZN_CALL_COMPLETE_COOKIE, true, {
              expires: 1,
            })
          }
        } catch (error) {
          logger.error('Error sending data:', error)
        }

        // Check if pznCompleted is now true after the attempt
        const isPznCompleted = Cookies.get(PZN_CALL_COMPLETE_COOKIE)
        if (retryCount >= MAX_RETRIES) {
          logger.error('Error after Max tries :', ehVisitId, browserId)
        }
        if (!isPznCompleted && retryCount < MAX_RETRIES) {
          retryCount++
          const delay = INITIAL_DELAY * Math.pow(2, retryCount - 1)
          timerId = setTimeout(sendData, delay)
        }
      }

      timerId = setTimeout(sendData, INITIAL_DELAY)
      return () => {
        if (timerId) clearTimeout(timerId)
      }
    }
  }, [isPageLoaded, asPath, fireAndForget, pznCompleted])
  return (
    showModal && (
      <PznModal
        onClose={() => {
          setShowModal(false)
        }}
      />
    )
  )
}

const PznModal = function ({ onClose }) {
  // for fire and forget events
  const { fireAndForget } = useTriggerEvents()
  useEffect(() => {
    fireAndForget({
      event_type: 'app_event',
      event_action: 'app_event',
      nav_link_section: `Direct Mail PZN Pop Up`,
      track_event: 'ahl_dm_ui_trigger_shown',
      list_name: 'Direct mail PZN UI loaded',
    })

    // Re-enable scrolling when the component is unmounted
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [fireAndForget])

  return (
    <div className="fixed top-0 left-0 w-screen h-screen bg-black/50 flex justify-center items-center z-9999">
      <div
        className="bg-white text-black rounded-[16px] border-0 min-w-[335px] max-w-[335px] min-h-[575px] flex flex-col items-start relative md:min-w-[412px] md:max-w-[412px]"
        variant="elevation"
      >
        <div className="w-full flex justify-center items-center pt-5 pb-3 px-[13px] md:py-[22px] md:px-[22px]">
          <TrackingWrapper
            track={{
              event_type: 'ui_click',
              event_action: 'ui_click',
              nav_link_section: `Direct Mail PZN Pop Up`,
              track_event: 'ahl_dm_ui_dismissed',
              list_name: 'Direct mail PZN Pop Up dismissed',
            }}
            onClick={onClose}
          >
            <div className="cursor-pointer min-w-[24px] min-h-[24px] ml-auto flex justify-center items-center">
              <X
                size={20}
                color={'black'}
                preload={false}
                aria-hidden={true}
                alt="Close button"
                data-testid="pfq-x-icon"
              />
            </div>
          </TrackingWrapper>
        </div>
        <div className="p-6 pt-0">
          <div className="w-full flex justify-center items-center pb-5">
            <EnvelopeOk />
          </div>
          <div>
            <Typography
              variant="displayMd"
              content={`Got a mailer from Achieve Loans?`}
              className="pb-6 md:text-[32px]"
            />
            <Typography
              variant="displayXxs"
              content={`Enter your personal code to check your exclusive Home Equity Loan offer — up to $300,000.`}
              className="pb-6 text-[#42546B]"
            />

            <LinkButton
              track={{
                list_name: 'Direct mail PZN click',
                nav_link_section: `Direct Mail PZN Pop Up`,
                click_id: 'Enter my code',
                click_text: 'Enter my code',
                click_url: '/FixDebt',
                track_event: 'ahl_dm_ui_cta_clicked',
                event_action: 'button_click',
                event_type: 'button_click',
              }}
              onClick={onClose}
              typographicOptions={{
                variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
                fontWeight: 'bold',
              }}
              variant="contained"
              color="primary"
              content={{ linkHref: '/FixDebt', linkText: 'Enter my code' }}
              className={'w-full'}
              underline="hover"
            />
            <Typography
              variant="displayXxs"
              content={`Can’t find your code? Give us a call`}
              className="py-6 text-[12px] text-[#42546B] text-center"
            />
            <Button
              color="secondary"
              variant="text"
              className="w-full flex flex-row  "
              size="small"
              onClick={() => window.open(`tel:(833) 582-3382`, '_self')}
            >
              <MediaImageStandard
                className="object-contain relative hue-rotate-[-50deg]"
                alt={'phone icon'}
                height={20}
                content={{ url: '/multi-site-assets/icon-phone.svg' }}
                width={20}
                layout="fill"
              />
              <Typography
                content={`(833) 582-3382`}
                className=" text-[14px] font-bold text-[#154199] text-center"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DirectMailPzn
